import * as XLSX from "xlsx";

/**
 * Result of XLSX to JSON conversion
 */
export interface XlsxToJsonResult {
  /**
   * The converted data as an array of objects
   */
  data: Record<string, unknown>[];
}

/**
 * Result of XLSX to CSV conversion
 */
export interface XlsxToCsvResult {
  /**
   * The converted data as a CSV string
   */
  data: string;
}

/**
 * Internal helper to read the first worksheet from an XLSX file
 */
const readFirstWorksheet = async (file: File): Promise<XLSX.WorkSheet> => {
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: "array" });
  const firstSheetName = workbook.SheetNames[0];

  if (!firstSheetName) {
    throw new Error("The XLSX file contains no sheets");
  }

  const worksheet = workbook.Sheets[firstSheetName];

  if (!worksheet) {
    throw new Error("Failed to read the first sheet");
  }

  return worksheet;
};

/**
 * Converts an XLSX file to JSON format (reads the first sheet only)
 *
 * @param file - The XLSX file to read
 * @returns Promise that resolves to the conversion result with data as array of objects
 *
 * @example
 * ```typescript
 * const result = await xlsxToJson(file);
 * console.log(result.data); // [{ Name: "John", Age: "30" }, ...]
 * ```
 */
export const xlsxToJson = async (file: File): Promise<XlsxToJsonResult> => {
  const worksheet = await readFirstWorksheet(file);

  // Convert to JSON with first row as headers
  const jsonData = XLSX.utils.sheet_to_json(worksheet, {
    raw: false, // Format values as strings for consistency
  });

  // Cast to the expected type
  const data = jsonData as Record<string, unknown>[];

  return { data };
};

/**
 * Converts an XLSX file to CSV format (reads the first sheet only)
 *
 * @param file - The XLSX file to read
 * @param delimiter - CSV delimiter (default: ",")
 * @returns Promise that resolves to the conversion result with data as CSV string
 *
 * @example
 * ```typescript
 * const result = await xlsxToCsv(file);
 * console.log(result.data); // "Name,Age\nJohn,30\nJane,25"
 *
 * // Use semicolon delimiter
 * const result2 = await xlsxToCsv(file, ";");
 * ```
 */
export const xlsxToCsv = async (file: File, delimiter = ","): Promise<XlsxToCsvResult> => {
  const worksheet = await readFirstWorksheet(file);

  // Convert to CSV
  const data = XLSX.utils.sheet_to_csv(worksheet, {
    FS: delimiter,
    RS: "\n",
  });

  return { data };
};

/**
 * Checks if a file is an Excel file based on its extension
 *
 * @param file - The file to check
 * @returns True if the file is an Excel file (.xlsx, .xls, .xlsm, .xlsb)
 */
export const isExcelFile = (file: File): boolean => {
  const fileName = file.name.toLowerCase();
  return fileName.endsWith(".xlsx") || fileName.endsWith(".xls") || fileName.endsWith(".xlsm") || fileName.endsWith(".xlsb");
};
